/* eslint-disable react/no-unknown-property */

import { Html, Line } from '@react-three/drei'
import { useMemo } from 'react'
import { ConeGeometry, CylinderGeometry, Vector3 } from 'three'

// Values that define the arrow shape
const leadingLineWidth = 1

// Values that define the arrow head tip shape
const arrowHeadTipRadius = 1.5
const arrowHeadTipLength = 6

// Values that define the arrow head trunk shape
const arrowHeadTrunkRadius = 0.5
const arrowHeadTrunkLength = 8

// Defines the position of the label along the arrow, starting from
// the origin. 0.8 is 80% of the way along the arrow
const arrowLabelPlacementMultiplier = 0.5

type PropTypes = {
  points: [number, number, number][]
  label?: string
  color?: string | number
}

const ForceArrow3D = ({ points, label, color = 'black' }: PropTypes) => {
  const labelPoint = useMemo(() => {
    const lastPoint = points[points.length - 1]
    const firstPoint = points[0]

    const lastPointVector = new Vector3(...lastPoint)
    const firstPointVector = new Vector3(...firstPoint)

    // Subtracts the end from the start to get the direction of the line, not normalised as we want the length.
    // Then we multiply by the placement multiplier to get a point along the total length of the line
    const pointAlongLine = lastPointVector.clone().sub(firstPointVector).multiplyScalar(arrowLabelPlacementMultiplier)

    // Get the point relative to the end of the arrow, rather than the origin
    const labelPoint = lastPointVector.clone().sub(pointAlongLine.clone())

    return labelPoint.toArray() as [number, number, number]
  }, [points])

  const arrowHeadTipModel = useMemo(() => {
    const lastPoint = points[points.length - 1]
    const secondLastPoint = points[points.length - 2]

    const lastPointVector = new Vector3(...lastPoint)
    const secondLastPointVector = new Vector3(...secondLastPoint)

    // Calculate a direction vector from the last point to the second last point
    const direction = lastPointVector.clone().sub(secondLastPointVector).normalize()
    const distance = lastPointVector.distanceTo(secondLastPointVector)

    // Tip length should never be longer than the arrow itself
    const tipLength = distance > arrowHeadTipLength ? arrowHeadTipLength : distance

    const point = new ConeGeometry(arrowHeadTipRadius, tipLength)

    // Get an origin for the cone model, half it's own length off the end of the line
    const origin = direction.clone().multiplyScalar(tipLength / 2)

    // First rotate the point, as the lookAt function orients the positive Z axis towards the target
    point.rotateX(Math.PI / 2)
    point.lookAt(direction)

    // Translate the point to the end of the line
    point.translate(lastPointVector.x, lastPointVector.y, lastPointVector.z)

    // Further translate the point by half it's length off the end of the line to remove overlaps
    point.translate(origin.x, origin.y, origin.z)

    return point
  }, [points])

  const arrowHeadTrunkModel = useMemo(() => {
    const lastPoint = points[points.length - 1]
    const secondLastPoint = points[points.length - 2]

    const lastPointVector = new Vector3(...lastPoint)
    const secondLastPointVector = new Vector3(...secondLastPoint)

    // Calculate a direction vector from the last point to the second last point
    const direction = lastPointVector.clone().sub(secondLastPointVector).normalize()
    const distance = lastPointVector.distanceTo(secondLastPointVector)

    // Trunk length should never be longer than the arrow itself
    const trunkLength = distance > arrowHeadTrunkLength ? arrowHeadTrunkLength : distance

    const point = new CylinderGeometry(arrowHeadTrunkRadius, arrowHeadTrunkRadius, trunkLength)

    // Get an origin for the cone model, half it's own length away from the end of the line
    const origin = direction.clone().multiplyScalar(-(trunkLength / 2))

    // First rotate the point, as the lookAt function orients the positive Z axis towards the target
    point.rotateX(Math.PI / 2)
    point.lookAt(direction)

    // Translate the point to the end of the line
    point.translate(lastPointVector.x, lastPointVector.y, lastPointVector.z)

    // Further translate the point by half it's length away from the end of the line to remove overlaps
    point.translate(origin.x, origin.y, origin.z)

    return point
  }, [points])

  return (
    <group>
      <mesh>
        <primitive object={arrowHeadTipModel} />
        <meshStandardMaterial color={color} />
      </mesh>
      <mesh>
        <primitive object={arrowHeadTrunkModel} />
        <meshStandardMaterial color={color} />
      </mesh>
      <Line
        points={points}
        color={color}
        lineWidth={leadingLineWidth}
      />
      <Html
        position={labelPoint}
        center
      >
        <div
          className="bg-white bg-opacity-90 px-1 rounded cursor-default select-none"
        >
          {label}
        </div>
      </Html>
    </group>
  )
}

export default ForceArrow3D
